import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, User, Calendar, Trash2 } from 'lucide-react';
import { ResponseWithAnswersDto } from '../../services/feedbackService';
import { OccurrenceSummary } from '../../types/analytics';

interface GroupedResponsesListProps {
  responses: ResponseWithAnswersDto[];
  occurrenceSummaries?: OccurrenceSummary[];
  onResponseClick: (response: ResponseWithAnswersDto) => void;
  onDeleteResponse?: (responseId: string) => void;
  isDeleting?: boolean;
}

interface ResponseGroup {
  key: string;
  displayName: string;
  email: string;
  responses: ResponseWithAnswersDto[];
}

const GroupedResponsesList: React.FC<GroupedResponsesListProps> = ({
  responses,
  occurrenceSummaries,
  onResponseClick,
  onDeleteResponse,
  isDeleting,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const groupedResponses = useMemo<ResponseGroup[]>(() => {
    const groups = new Map<string, ResponseGroup>();

    responses.forEach((response) => {
      const respondentId = response.respondent?.id;
      const email = response.respondent?.email || '';
      const name = response.respondent?.fullName || '';
      const key = respondentId ? `id:${respondentId}` : email ? `email:${email.toLowerCase()}` : 'anonymous';

      const existingGroup = groups.get(key);
      if (existingGroup) {
        existingGroup.responses.push(response);
        return;
      }

      groups.set(key, {
        key,
        displayName: name || email || 'Anonymous',
        email: email || '-',
        responses: [response],
      });
    });

    const grouped = Array.from(groups.values()).map((group) => ({
      ...group,
      responses: [...group.responses].sort((a, b) => {
        const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
        const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
        return aTime - bTime;
      }),
    }));

    return grouped.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [responses]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedResponses;
    const lowerSearch = searchTerm.toLowerCase();

    return groupedResponses.filter(
      (group) =>
        group.displayName.toLowerCase().includes(lowerSearch) ||
        group.email.toLowerCase().includes(lowerSearch)
    );
  }, [groupedResponses, searchTerm]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    if (groupedResponses.length === 0) return {};
    return { [groupedResponses[0].key]: true };
  });

  const handleToggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleDeleteClick = (e: React.MouseEvent, responseId: string) => {
    e.stopPropagation();
    setConfirmDeleteId(responseId);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDeleteId && onDeleteResponse) {
      onDeleteResponse(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  const getOccurrenceLabel = (submittedAt?: string) => {
    if (!submittedAt) return null;

    const submittedTime = new Date(submittedAt).getTime();
    if (!occurrenceSummaries || occurrenceSummaries.length === 0) return null;

    const match = occurrenceSummaries.find((occurrence) => {
      const startsAt = new Date(occurrence.startsAt).getTime();
      const endsAt = occurrence.endsAt ? new Date(occurrence.endsAt).getTime() : Number.POSITIVE_INFINITY;
      return submittedTime >= startsAt && submittedTime < endsAt;
    });

    if (!match) return null;
    return `Occurrence ${match.occurrenceNumber}`;
  };

  if (responses.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-400">No responses yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div className="space-y-3">
        {filteredGroups.map((group) => {
          const isExpanded = !!expandedGroups[group.key];

          return (
            <div key={group.key} className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button
                type="button"
                onClick={() => handleToggleGroup(group.key)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center min-w-0">
                    <div className="mr-3 text-gray-500 dark:text-gray-400">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="ml-4 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{group.displayName}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{group.email}</div>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {group.responses.length} {group.responses.length === 1 ? 'response' : 'responses'}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {group.responses.map((response) => {
                      const occurrenceLabel = getOccurrenceLabel(response.submittedAt);

                      return (
                        <div
                          key={response.id}
                          onClick={() => onResponseClick(response)}
                          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-2 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {occurrenceLabel && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                    {occurrenceLabel}
                                  </span>
                                )}
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {response.answers?.length || 0} answers
                                </span>
                              </div>
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Calendar className="h-4 w-4 mr-2" />
                                {response.submittedAt
                                  ? new Date(response.submittedAt).toLocaleDateString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : 'In Progress'}
                              </div>
                            </div>

                            {onDeleteResponse && (
                              <div onClick={(e) => e.stopPropagation()}>
                                {confirmDeleteId === response.id ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-sm text-red-600 dark:text-red-400">Delete?</span>
                                    <button
                                      onClick={handleConfirmDelete}
                                      disabled={isDeleting}
                                      className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                                    >
                                      {isDeleting ? 'Deleting...' : 'Yes'}
                                    </button>
                                    <button
                                      onClick={handleCancelDelete}
                                      disabled={isDeleting}
                                      className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded disabled:opacity-50"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => handleDeleteClick(e, response.id)}
                                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    title="Delete response"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredGroups.length === 0 && searchTerm && (
        <div className="text-center py-8 border border-gray-200 dark:border-gray-700 rounded-lg">
          <p className="text-gray-600 dark:text-gray-400">No responses matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default GroupedResponsesList;
